import React from 'react';
import { Body5, Body7, Container } from '@ui';
import { PREVIEW_STATUS } from '@services/scoreSpecs';
import { formatUtcDateTime, isPrintableUtc } from './format';
import {
  CONTEXT_KIND,
  companyDetail,
  companyLabel,
  contextSummary,
  hasNoScores,
  isKnownStatus,
  statusLabel,
  statusTone,
} from './previewResult';
import { Chip, Consequence, MicroLabel, PassiveCard, ReadOnlyJson, Row } from './styles';

// One company-unit off a preview run, read out in the order an operator debugs it: who it is, what
// it read, what came back raw, what extraction made of it.
//
// Since fapi #492 a result row is a COMPANY-UNIT, not a document, and it arrives under one of three
// statuses that mean three different jobs. The render is a chain of branches that STOP rather than
// a single tree with holes in it: a `failed_resolve` row never reaches the extraction copy, because
// every sentence there blames a prompt for what is a retrieval failure. A status this editor does
// not recognise stops before any of it — a fourth status shipped by the engine must never be
// rendered as a success by default.

// An extra column can hold a whole paragraph of `thinking`, and one of those on a single line
// destroys the dock. The full text is already visible in `raw_output` above, so the line here is a
// pointer, not the record.
const MAX_EXTRA_CHARS = 160;
const renderExtraValue = (value) => {
  const text = JSON.stringify(value);
  return text.length > MAX_EXTRA_CHARS ? `${text.slice(0, MAX_EXTRA_CHARS)}…` : text;
};

const Identity = ({ result }) => {
  const detail = companyDetail(result);
  // Null for a status this editor does not know. An empty chip reads as a blank badge next to the
  // company name; the warn line under it carries the raw value instead.
  const label = statusLabel(result.status);
  return (
    <>
      <Row>
        <Body5 bold>{companyLabel(result)}</Body5>
        {label && <Chip $tone={statusTone(result.status)}>{label}</Chip>}
      </Row>
      {detail && (
        <Container top={4}>
          <Body7 isLowContrast>{detail}</Body7>
        </Container>
      )}
    </>
  );
};

const ContextBlock = ({ result }) => {
  const { kind, ids, count } = contextSummary(result);
  const { sourcePublishedAt, maxContextPublishedAt } = result;
  // Printability, not truthiness: an unparseable timestamp is truthy but formats to an em dash, and
  // gating on the value would render 'Trigger published —' as though a date had been reported.
  const hasTrigger = isPrintableUtc(sourcePublishedAt);
  const hasContext = isPrintableUtc(maxContextPublishedAt);

  return (
    <Container top={16}>
      <MicroLabel>What it read</MicroLabel>
      <Container top={6}>
        {kind === CONTEXT_KIND.DOCUMENTS && (
          <Body7>{`${count} document${count === 1 ? '' : 's'}: ${ids.join(', ')}`}</Body7>
        )}
        {kind === CONTEXT_KIND.EMPTY &&
          // On a failed_resolve row no model call was made at all, so the sentence below — which
          // says the model was asked — would invent one.
          (result.status === PREVIEW_STATUS.FAILED_RESOLVE ? (
            <Body7 isLowContrast>No documents resolved, so nothing was sent to the model.</Body7>
          ) : (
            <Body7 isLowContrast>
              No documents. The model was asked with an empty context, so whatever it returned, it did not read
              anything.
            </Body7>
          ))}
        {kind === CONTEXT_KIND.NOT_REPORTED && (
          <Body7 isLowContrast>The engine did not report which documents fed this run.</Body7>
        )}
      </Container>

      {/* Both timestamps, side by side, and nothing more. A look-ahead check is deliberately NOT
          computed here: whether max_context_published_at covers the documents actually sent or the
          whole candidate set is unconfirmed with fapi, and a false leak warning on a healthy row
          costs more trust than the missing warning costs. */}
      {(hasTrigger || hasContext) && (
        <Container top={6}>
          {hasTrigger && hasContext ? (
            <Body7 isLowContrast>
              {`Trigger published ${formatUtcDateTime(sourcePublishedAt)} · latest context document ${formatUtcDateTime(
                maxContextPublishedAt
              )}`}
            </Body7>
          ) : (
            <Body7 isLowContrast>
              {`${
                hasTrigger
                  ? `Trigger published ${formatUtcDateTime(sourcePublishedAt)}`
                  : `Latest context document ${formatUtcDateTime(maxContextPublishedAt)}`
              }. The engine reported no second timestamp, so the two cannot be compared here.`}
            </Body7>
          )}
        </Container>
      )}
    </Container>
  );
};

const FailureBlock = ({ result }) => {
  const { errorClass, errorText } = result;
  if (!errorClass && !errorText) return null;
  return (
    <Container top={12}>
      <MicroLabel>Why it failed</MicroLabel>
      {errorClass && (
        <Container top={6}>
          <Chip $tone="bad">{errorClass}</Chip>
        </Container>
      )}
      {/* `error_text` is server-authored and unbounded — a traceback on one line destroys the dock,
          which is the whole reason MAX_EXTRA_CHARS exists for the extras below. It goes in the
          scrolling pre, which wraps and clips nothing, never on a Body7 line. */}
      {errorText && (
        <Container top={6}>
          <ReadOnlyJson>{errorText}</ReadOnlyJson>
        </Container>
      )}
    </Container>
  );
};

const RawOutputBlock = ({ result }) => {
  const isEmpty = result.rawOutput === null || result.rawOutput === undefined;
  return (
    <Container top={16}>
      <MicroLabel>Raw output</MicroLabel>
      <Container top={6}>
        <ReadOnlyJson>{isEmpty ? 'empty' : JSON.stringify(result.rawOutput, null, 2)}</ReadOnlyJson>
      </Container>
      {isEmpty && (
        <Container top={6}>
          <Consequence $tone="warn">
            Empty. A result row carries no finish_reason and no token counts at all, so this looks the same whether the
            model had nothing to say or ran out of output budget mid-answer. If max_output_tokens is tight for a
            reasoning model, that is the first thing to rule out.
          </Consequence>
        </Container>
      )}
    </Container>
  );
};

const ScoresBlock = ({ result }) => {
  const { scores } = result;
  const names = scores ? Object.keys(scores) : [];
  return (
    <Container top={16}>
      <MicroLabel>What extraction got</MicroLabel>
      <Container top={6}>
        {/* Warn, not bad: the engine called this company completed, so the missing scores are a
            disagreement between the row's own two fields, not a failure the row admits to. */}
        {!scores && (
          <Consequence $tone="warn">The engine reported this company as completed but returned no scores.</Consequence>
        )}
        {hasNoScores(result) && (
          <Consequence $tone="warn">
            No scores were recorded, although the engine reported this company as completed.
          </Consequence>
        )}
        {names.map((name) => (
          <Body7 key={name}>{`${name} → ${String(scores[name])}`}</Body7>
        ))}
      </Container>
    </Container>
  );
};

const ExtraColumnsBlock = ({ result }) => {
  // Rendered from the row and nothing else. The old cross-read against the buffer's declared
  // extra_columns existed only to tell "not evaluated" apart from "none declared" on a wire that
  // could not say which — per-row `status` says it outright now, and a failed row never reaches
  // this block at all.
  const extras = result.signalPayload && typeof result.signalPayload === 'object' ? result.signalPayload : {};
  const extraNames = Object.keys(extras);
  if (extraNames.length === 0) return null;

  const anyNull = extraNames.some((n) => extras[n] === null || extras[n] === undefined);
  const anyList = extraNames.some((n) => Array.isArray(extras[n]));

  return (
    <Container top={16}>
      <MicroLabel>Extra columns</MicroLabel>
      <Container top={6}>
        {extraNames.map((name) => {
          const value = extras[name];
          if (value === null || value === undefined) {
            return <Body7 key={name} isLowContrast>{`${name} → null`}</Body7>;
          }
          if (Array.isArray(value)) {
            return (
              <Body7 key={name}>{`${name} → ${value.length} matches, stored as a list: ${renderExtraValue(
                value
              )}`}</Body7>
            );
          }
          return <Body7 key={name}>{`${name} → ${renderExtraValue(value)}`}</Body7>;
        })}
      </Container>
      {anyNull && (
        <Container top={6}>
          <Consequence>
            A null means the path matched nothing, or matched a node that was itself null — the engine collapses both.
            Extras are best-effort, so a miss is recorded and the company-unit is still scored.
          </Consequence>
        </Container>
      )}
      {anyList && (
        <Container top={6}>
          <Consequence $tone="warn">
            A path that matches more than one node is stored as a list, not a scalar. Anything reading that column out
            of signal_payload and expecting a single value will get an array.
          </Consequence>
        </Container>
      )}
    </Container>
  );
};

const PerScoreExtrasBlock = ({ result }) => {
  // {score_name: {column: value}}. Kept visually apart from the broadcast block above because the
  // scope is the whole point — a column here reaches one feed, a column there reaches every feed
  // this spec publishes.
  const perScore =
    result.perScorePayloads && typeof result.perScorePayloads === 'object' ? result.perScorePayloads : {};
  const scoreNames = Object.keys(perScore).filter(
    (scoreName) => perScore[scoreName] && typeof perScore[scoreName] === 'object'
  );
  if (scoreNames.length === 0) return null;

  return (
    <Container top={16}>
      <MicroLabel>Per-score extra columns</MicroLabel>
      <Container top={6}>
        <Consequence>
          Each of these lands in that score’s own row, so it appears on that score’s feed only. The block above
          broadcasts to every feed this spec publishes.
        </Consequence>
      </Container>
      {scoreNames.map((scoreName) => {
        const columns = perScore[scoreName];
        const columnNames = Object.keys(columns);
        return (
          <Container key={`per-score-${scoreName}`} top={8}>
            <Body7 bold>{scoreName}</Body7>
            {columnNames.length === 0 ? (
              <Body7 isLowContrast>no columns declared for this score</Body7>
            ) : (
              columnNames.map((columnName) => {
                const value = columns[columnName];
                if (value === null || value === undefined) {
                  return <Body7 key={columnName} isLowContrast>{`${columnName} → null`}</Body7>;
                }
                return <Body7 key={columnName}>{`${columnName} → ${renderExtraValue(value)}`}</Body7>;
              })
            )}
          </Container>
        );
      })}
    </Container>
  );
};

export const PreviewResultCard = ({ result }) => {
  if (!result) return null;

  const { status } = result;

  // First branch and a hard stop. Every block below reads a field whose meaning is fixed by one of
  // the three known statuses; under a fourth one they would all be guesses presented as findings.
  if (!isKnownStatus(status)) {
    return (
      <PassiveCard>
        <Identity result={result} />
        <Container top={8}>
          <Consequence $tone="warn">
            {`The engine reported a status this editor does not know: ${String(
              status
            )}. Nothing below has been interpreted.`}
          </Consequence>
        </Container>
      </PassiveCard>
    );
  }

  // Nothing resolved, so nothing was sent and no model ran. The raw-output block is suppressed
  // rather than shown as 'empty': its copy reads an empty output as a possibly-truncated model
  // answer, which would send the operator to max_output_tokens for a run that never happened.
  if (status === PREVIEW_STATUS.FAILED_RESOLVE) {
    return (
      <PassiveCard>
        <Identity result={result} />
        <ContextBlock result={result} />
        <Container top={16}>
          <Consequence $tone="warn">
            The trigger fired for this company, but nothing resolved to read. Nothing was sent to the model. That is a
            retrieval problem, not a prompt problem — check the document set, not the wording.
          </Consequence>
        </Container>
        {/* FailureBlock guards itself on both fields being absent. Gating on errorText alone here
            would hide a row that reported only an error_class, which is the half that names the
            failure kind. */}
        <FailureBlock result={result} />
      </PassiveCard>
    );
  }

  const isFailed = status === PREVIEW_STATUS.FAILED;

  return (
    <PassiveCard>
      <Identity result={result} />
      <ContextBlock result={result} />

      {isFailed && (
        <>
          <Container top={16}>
            <Consequence $tone="bad">
              Extraction ran and failed. Score extraction is all-or-nothing, so nothing was recorded for this company.
            </Consequence>
          </Container>
          <FailureBlock result={result} />
        </>
      )}

      {/* A failed row still shows its raw output — that text is the thing being debugged. */}
      <RawOutputBlock result={result} />

      {isFailed ? (
        <Container top={16}>
          <Body7 isLowContrast>
            Payload columns were not evaluated — extraction short-circuits the whole company-unit.
          </Body7>
        </Container>
      ) : (
        <>
          <ScoresBlock result={result} />
          <ExtraColumnsBlock result={result} />
          <PerScoreExtrasBlock result={result} />
        </>
      )}
    </PassiveCard>
  );
};
